import React, { MouseEvent, useMemo, useCallback, FC, ChangeEvent, createRef, useState, DragEvent } from 'react';
import { FileUploadIcon } from './FileUploadIcon';
import { BpDesignSystem } from '../../types';
import { useIntlBds } from '../../translations/BdsIntlProvider';
import { FormLabel } from '../Form/FormLabel';
import { styled } from '../../styles';
import { tokenToPx } from '../../setup/theme/impl/getToken';
import { Box } from '../Box/Box';

export type FileUploadBoxProps = BpDesignSystem.PropsWithChildren<{
  /**
   * **REQUIRED** - Set to optional for backwards compatibility.
   * A screen with more than one FileUploadBox element will not work properly without this prop.
   */
  inputId?: string;
  onUpload?(file?: File): void;
  /** Only expected to trigger if `allowedExtensions` list unsupported by browser or bypassed. */
  onError?(reason?: string): void;
  /** Has default value in English. */
  instructionHeader?: string;
  description?: string;
  fileSizeLimitMessage?: string;
  padding?: string;
  icon?: JSX.Element;
  allowedExtensions?: `.${string}`[];
  dataTest?: string;
  maxBytes?: number;
  disabled?: boolean;
}>;

export const FileUploadBox: FC<FileUploadBoxProps> = ({
  inputId = 'uploadFile',
  onUpload,
  onError,
  instructionHeader,
  description,
  padding,
  icon,
  allowedExtensions,
  dataTest,
  fileSizeLimitMessage = 'File size exceeds limit',
  maxBytes,
  disabled,
  ...props
}) => {
  const { intl } = useIntlBds();
  const [drag, setDrag] = useState(false);
  const allowedExtensionsInput = allowedExtensions?.join(', ');
  const allowedExtensionsRegex = useMemo(
    () => new RegExp(`(${allowedExtensions?.join('|\\') ?? ''})$`, 'gi'),
    [allowedExtensions]
  );

  const onUploadClick = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files.length > 0) {
        const file = event.target.files[0];
        if (maxBytes && file?.size && file?.size > maxBytes) {
          onError?.(fileSizeLimitMessage);
        } else if (file?.name && allowedExtensionsRegex.test(file.name)) {
          onUpload?.(file);
        } else {
          onError?.();
        }
      }
    },
    [allowedExtensionsRegex, onError, maxBytes, fileSizeLimitMessage, onUpload]
  );

  const dropRef = createRef<HTMLDivElement>();

  const handleDrag = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragIn = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setDrag(true);
    }
  }, []);

  const handleDragOut = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDrag(false);
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setDrag(false);
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const file = e.dataTransfer.files[0];
        if (file?.name && allowedExtensionsRegex.exec(file.name)) {
          onUpload?.(file);
        } else {
          onError?.();
        }
      }
    },
    [allowedExtensionsRegex, onError, onUpload]
  );

  const handleInputClick = useCallback((event: MouseEvent<HTMLInputElement>) => {
    event.currentTarget.value = '';
  }, []);

  return (
    <StyledLabel component="label" htmlFor={inputId} data-test={dataTest} disabled={disabled} {...props}>
      <StyledDropBox
        $padding={padding}
        ref={dropRef}
        onDragOver={handleDrag}
        onDragEnter={handleDragIn}
        onDragLeave={handleDragOut}
        onDrop={handleDrop}
        $isDragging={drag}
        $disabled={disabled}
      >
        <StyledUploadInput
          id={inputId}
          type="file"
          accept={allowedExtensionsInput}
          onChange={onUploadClick}
          onClick={handleInputClick}
        />
        {icon || <FileUploadIcon />}
        {<StyledHeader>{instructionHeader ?? intl.FileUploadBox.instructionHeader}</StyledHeader>}
        {description && <StyledDescription>{description}</StyledDescription>}
      </StyledDropBox>
    </StyledLabel>
  );
};

const StyledHeader = styled('div')`
  font-weight: ${({ theme }) => theme.tokens.fontWeightMedium};
  font-size: ${tokenToPx('bodyMediumFontSize')};
  text-align: center;
  margin: ${({ theme }) => theme.spacingSize.m} 0;
  color: ${({ theme }) => theme.tokens.colorOnSurface};
  pointer-events: none;
`;

const StyledDescription = styled('div')`
  font-size: ${tokenToPx('labelSmallFontSize')};
  text-align: center;
  color: ${({ theme }) => theme.tokens.colorOnSurfaceVariant};
`;

/**
 * Undisplayed input element is still intractable via its label.
 *
 * Other methods of hiding the element can break the styling of certain screens.
 */
const StyledUploadInput = styled('input')`
  display: none;
`;

const StyledLabel = styled(FormLabel)`
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
`;

const StyledDropBox = styled(Box)<{ $padding?: string; $isDragging?: boolean; $disabled?: boolean }>`
  min-height: 250px;
  margin: 1.5% 0;
  z-index: 3;
  background-color:  ${({ theme }) => theme.tokens.colorSurface};
  width: 100%;
  padding: ${({ $padding }) => $padding || '2rem'};
  border: ${({ $isDragging }) => ($isDragging ? `2px` : `1px`)} dashed
    ${({ $isDragging, theme }) => ($isDragging ? theme.tokens.colorPrimary : theme.tokens.colorOutline)};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: ${({ $isDragging, theme }) => ($isDragging ? theme.tokens.shadowModerate : `none`)};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
`;
